import { Check } from "lucide-react";
import { useFormStore } from "../../store/formStore";
import type { IntakeFormData } from "../../types";

type BooleanField = {
  [K in keyof IntakeFormData]: IntakeFormData[K] extends boolean ? K : never;
}[keyof IntakeFormData];

interface CheckboxInputProps {
  field: BooleanField;
  label: string;
  /** Optional longer helper text rendered below the label. */
  description?: string;
  containerClassName?: string;
  /** "pill" renders as a clickable pill button with a built-in check indicator. */
  variant?: "default" | "pill";
}

/**
 * Bound checkbox input. Reads/writes directly from the store — checkboxes don't
 * go through useFormField because their onChange shape differs (no string value)
 * and they can't be prefilled from proposal baseline in a meaningful way.
 *
 * Usage:
 *   <CheckboxInput field="hasCba" label="Employees covered under a CBA?" />
 *   <CheckboxInput field="fiduciarySameAsContact" label="Same as primary contact" />
 */
export function CheckboxInput({ field, label, description, containerClassName, variant = "default" }: CheckboxInputProps) {
  const value = useFormStore((s) => s.data[field]);
  const setField = useFormStore((s) => s.setField);

  if (variant === "pill") {
    return (
      <button
        type="button"
        role="checkbox"
        aria-checked={value}
        onClick={() => setField(field, !value)}
        className={`inline-flex items-center gap-2 px-3 py-2 rounded-lg border text-sm font-medium transition-all cursor-pointer select-none
          ${value
            ? "border-brand-green bg-brand-green-light text-brand-green-dark"
            : "border-gray-200 bg-white text-gray-600 hover:border-gray-300 hover:bg-gray-50"
          }`}
      >
        <span className={`w-4 h-4 flex-shrink-0 rounded border flex items-center justify-center
          ${value ? "bg-brand-green border-brand-green" : "border-gray-300"}`}>
          {value && <Check size={10} className="text-white" />}
        </span>
        {label}
      </button>
    );
  }

  return (
    <label
      className={`flex items-start gap-3 cursor-pointer select-none ${containerClassName ?? ""}`}
    >
      <input
        type="checkbox"
        checked={value}
        onChange={(e) => setField(field, e.currentTarget.checked)}
        className="mt-0.5 h-4 w-4 rounded border-gray-300 text-brand-green focus:ring-brand-green"
      />
      <div>
        <span className="text-sm text-gray-700">{label}</span>
        {description && <p className="text-xs text-gray-500 mt-0.5">{description}</p>}
      </div>
    </label>
  );
}
